import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Label,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Chip,
  Stack,
  Divider,
  Tooltip as MuiTooltip,
  Skeleton,
  Button,
  Paper,
  Avatar,
  Badge,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  PieChart as PieChartIcon,
  GridOn,
  ErrorOutline,
  ArrowUpward,
  ArrowDownward,
  MonetizationOn,
  ShoppingCart,
  Person,
  Group,
  Repeat,
  Download,
  TableChart,
  Refresh,
} from "@mui/icons-material";

const CustomerTypeRatio = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  // State management
  const [data, setData] = useState({
    newCustomers: 0,
    repeatCustomers: 0,
    newCustomerRevenue: 0,
    repeatCustomerRevenue: 0,
    newCustomerOrders: 0,
    repeatCustomerOrders: 0,
    historicalData: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("pie"); // 'pie', 'grid', or 'table'
  const [prevData, setPrevData] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    end: new Date(),
  });
  const [compareDateRange, setCompareDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 2)),
    end: new Date(new Date().setMonth(new Date().getMonth() - 1)),
  });
  const [chartAnimation, setChartAnimation] = useState(true);
  const [innerRadius, setInnerRadius] = useState(60);
  const [retryCount, setRetryCount] = useState(0);

  // Stats state
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalRevenue: 0,
    totalOrders: 0,
    newCustomerPercentage: 0,
    repeatCustomerPercentage: 0,
    avgNewCustomerValue: 0,
    avgRepeatCustomerValue: 0,
    growthRate: 0,
    retentionRate: 0,
  });

  // Configuration constants
  const CONFIG = {
    chart: {
      innerRadius: innerRadius,
      outerRadius: 90,
      legendPosition: {
        vertical: "middle",
        horizontal: "right",
      },
    },
    colors: {
      primary: [colors.blueAccent[500], colors.greenAccent[500]],
      highlight: colors.blueAccent[700],
      tooltipBg: theme.palette.background.paper,
      cardBg: theme.palette.background.default,
      success: colors.greenAccent[500],
      warning: colors.redAccent[500],
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
    },
  };

  // Generate sample data for demo purposes
  const generateSampleData = () => {
    return {
      newCustomers: 1250,
      repeatCustomers: 750,
      newCustomerRevenue: 45000,
      repeatCustomerRevenue: 75000,
      newCustomerOrders: 800,
      repeatCustomerOrders: 1200,
      historicalData: [
        { month: "Jan", new: 300, repeat: 150 },
        { month: "Feb", new: 350, repeat: 180 },
        { month: "Mar", new: 400, repeat: 200 },
        { month: "Apr", new: 450, repeat: 220 },
      ],
    };
  };

  // Check if API data is empty
  const isDataEmpty = (apiData) => {
    if (!apiData) return true;

    // Handle array response
    if (Array.isArray(apiData)) {
      return apiData.length === 0;
    }

    // Handle object response
    if (typeof apiData === "object") {
      return Object.keys(apiData).length === 0;
    }

    return true;
  };

  // Calculate statistics from data
  const calculateStats = (data) => {
    const totalCustomers = data.newCustomers + data.repeatCustomers;
    const totalRevenue = data.newCustomerRevenue + data.repeatCustomerRevenue;
    const totalOrders = data.newCustomerOrders + data.repeatCustomerOrders;
    const growthRate =
      data.historicalData.length > 1
        ? ((data.newCustomers -
            data.historicalData[data.historicalData.length - 2].new) /
            data.historicalData[data.historicalData.length - 2].new) *
          100
        : 0;
    const retentionRate =
      data.historicalData.length > 1
        ? (data.repeatCustomers /
            data.historicalData[data.historicalData.length - 2].new) *
          100
        : 0;

    return {
      totalCustomers,
      totalRevenue,
      totalOrders,
      newCustomerPercentage: Math.round(
        (data.newCustomers / totalCustomers) * 100
      ),
      repeatCustomerPercentage: Math.round(
        (data.repeatCustomers / totalCustomers) * 100
      ),
      avgNewCustomerValue:
        data.newCustomers > 0
          ? Math.round(data.newCustomerRevenue / data.newCustomers)
          : 0,
      avgRepeatCustomerValue:
        data.repeatCustomers > 0
          ? Math.round(data.repeatCustomerRevenue / data.repeatCustomers)
          : 0,
      growthRate: Math.round(growthRate),
      retentionRate: Math.round(retentionRate),
    };
  };

  // Calculate change percentage
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Process API response data with more robust handling
  const processApiData = (apiData) => {
    // console.log("Raw API Data:", apiData); // Debug log

    // Handle case where API returns null or undefined or empty data
    if (isDataEmpty(apiData)) {
      console.warn("API returned empty data, using sample data");
      return generateSampleData();
    }

    // Handle array response
    if (Array.isArray(apiData)) {
      // Find the most recent data point (assuming array is sorted)
      const latestData = apiData[apiData.length - 1];

      return {
        newCustomers:
          latestData.new_party_count ||
          latestData.newCustomers ||
          latestData.new ||
          0,
        repeatCustomers:
          latestData.repeat_party_count ||
          latestData.repeatCustomers ||
          latestData.repeat ||
          0,
        newCustomerRevenue:
          latestData.new_customer_revenue || latestData.newRevenue || 0,
        repeatCustomerRevenue:
          latestData.repeat_customer_revenue || latestData.repeatRevenue || 0,
        newCustomerOrders:
          latestData.new_customer_orders || latestData.newOrders || 0,
        repeatCustomerOrders:
          latestData.repeat_customer_orders || latestData.repeatOrders || 0,
        historicalData: apiData.map((item) => ({
          month: item.month || item.date || "N/A",
          new: item.new_party_count || item.newCustomers || item.new || 0,
          repeat:
            item.repeat_party_count || item.repeatCustomers || item.repeat || 0,
        })),
      };
    }

    // Handle object response (key-value pairs by date/month)
    if (typeof apiData === "object" && apiData !== null) {
      const keys = Object.keys(apiData);

      // Get most recent entry
      const latestKey = keys[keys.length - 1];
      const latestData = apiData[latestKey] || {};

      return {
        newCustomers:
          latestData.new_party_count ||
          latestData.newCustomers ||
          latestData.new ||
          0,
        repeatCustomers:
          latestData.repeat_party_count ||
          latestData.repeatCustomers ||
          latestData.repeat ||
          0,
        newCustomerRevenue:
          latestData.new_customer_revenue || latestData.newRevenue || 0,
        repeatCustomerRevenue:
          latestData.repeat_customer_revenue || latestData.repeatRevenue || 0,
        newCustomerOrders:
          latestData.new_customer_orders || latestData.newOrders || 0,
        repeatCustomerOrders:
          latestData.repeat_customer_orders || latestData.repeatOrders || 0,
        historicalData: Object.values(apiData).map((item) => ({
          month: item.month || item.date || "N/A",
          new: item.new_party_count || item.newCustomers || item.new || 0,
          repeat:
            item.repeat_party_count || item.repeatCustomers || item.repeat || 0,
        })),
      };
    }

    // Fallback for unexpected data format
    console.warn("Unexpected API data format, using sample data:", apiData);
    return generateSampleData();
  };

  // Fetch data from API with retry logic
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);

      // Store current data as previous before fetching new data
      setPrevData(data);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/newRepeatCustomers`,
        { cmpid },
        {
          headers: { Authorization: token },
        }
      );
      // console.log(response.data)

      // Check if API data is empty
      if (isDataEmpty(response.data)) {
        const sampleData = generateSampleData();
        setData(sampleData);
        setStats(calculateStats(sampleData));
        setIsUsingSampleData(true);
        setRetryCount(0);
        return;
      }

      const processedData = processApiData(response.data);
      setData(processedData);
      setStats(calculateStats(processedData));
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error("Error fetching customer type data:", err);

      if (retryCount < 2) {
        // Retry up to 2 times
        setRetryCount(retryCount + 1);
        setTimeout(fetchData, 2000 * retryCount); // Exponential backoff
        return;
      }

      setError("No data available. showing sample data");
      const sampleData = generateSampleData();
      setData(sampleData);
      setStats(calculateStats(sampleData));
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch comparison data
  const fetchComparisonData = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/newRepeatCustomers`,
        { cmpid },
        {
          headers: { Authorization: token },
          timeout: 10000,
        }
      );

      if (isDataEmpty(response.data)) {
        setCompareData(generateSampleData());
        return;
      }

      const processedData = processApiData(response.data);
      setCompareData(processedData);
    } catch (err) {
      console.error("Error fetching comparison data:", err);
      // Use sample data for comparison if API fails
      setCompareData(generateSampleData());
    }
  };

  // Toggle between views
  const toggleViewMode = () => {
    const views = ["pie", "grid", "table"];
    const currentIndex = views.indexOf(viewMode);
    setViewMode(views[(currentIndex + 1) % views.length]);
  };

  // Toggle comparison mode
  const toggleCompareMode = async () => {
    if (!compareMode) {
      await fetchComparisonData();
    }
    setCompareMode(!compareMode);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0].payload;
      const total = stats.totalCustomers;
      const percentage = Math.round((payload[0].value / total) * 100);

      return (
        <Card
          sx={{
            backgroundColor: CONFIG.colors.tooltipBg,
            boxShadow: theme.shadows[3],
            borderRadius: 2,
            p: 2,
            minWidth: 180,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {dataItem.name}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <Person fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Customers:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {dataItem.name === "New Customers"
                  ? data.newCustomers.toLocaleString()
                  : data.repeatCustomers.toLocaleString()}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <MonetizationOn fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Revenue:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                Rs.{" "}
                {dataItem.name === "New Customers"
                  ? data.newCustomerRevenue.toLocaleString()
                  : data.repeatCustomerRevenue.toLocaleString()}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <ShoppingCart fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Orders:
                </Typography>
              </Box>
              <Typography variant="body2">
                {dataItem.name === "New Customers"
                  ? data.newCustomerOrders.toLocaleString()
                  : data.repeatCustomerOrders.toLocaleString()}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Percentage:
              </Typography>
              <Typography variant="body2">{percentage}%</Typography>
            </Box>
          </Stack>
        </Card>
      );
    }
    return null;
  };

  // Custom legend formatter
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Effect to fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Effect to fetch comparison data when compare mode changes
  useEffect(() => {
    if (compareMode) {
      fetchComparisonData();
    }
  }, [compareMode]);

  // Prepare chart data - using customer counts since revenue data isn't available
  const chartData = [
    {
      name: "New Customers",
      value: data.newCustomers,
      color: CONFIG.colors.primary[0],
    },
    {
      name: "Repeat Customers",
      value: data.repeatCustomers,
      color: CONFIG.colors.primary[1],
    },
  ];

  // Prepare comparison chart data if in compare mode
  const comparisonChartData = compareData
    ? [
        {
          name: "New Customers",
          value: compareData.newCustomers,
          color: CONFIG.colors.primary[0],
        },
        {
          name: "Repeat Customers",
          value: compareData.repeatCustomers,
          color: CONFIG.colors.primary[1],
        },
      ]
    : null;

  // Calculate changes from previous data
  const totalRevenueChange = prevData
    ? calculateChange(stats.totalRevenue, calculateStats(prevData).totalRevenue)
    : 0;

  const totalCustomersChange = prevData
    ? calculateChange(
        stats.totalCustomers,
        calculateStats(prevData).totalCustomers
      )
    : 0;

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Export data to CSV
  const exportToCSV = () => {
    const headers = ["", "New Customers", "Repeat Customers", "Total"];
    const rows = [
      [
        "Customers",
        data.newCustomers,
        data.repeatCustomers,
        stats.totalCustomers,
      ],
      [
        "Revenue",
        data.newCustomerRevenue,
        data.repeatCustomerRevenue,
        stats.totalRevenue,
      ],
      [
        "Orders",
        data.newCustomerOrders,
        data.repeatCustomerOrders,
        stats.totalOrders,
      ],
    ];

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "customer_type_ratio.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 450,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: colors.primary[500],
                  width: 32,
                  height: 32,
                  boxShadow: theme.shadows[2],
                }}
              >
                <Group fontSize="small" />
              </Avatar>
              New vs. Repeat Customers
              <MuiTooltip title="Ratio and revenue impact of new vs. repeat customers">
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: CONFIG.colors.textSecondary,
                    cursor: "pointer",
                  }}
                />
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer segmentation by type and their revenue impact
              {/* {dateRange && (
                <span style={{ marginLeft: 8 }}>
                  ({formatDate(dateRange.start)} - {formatDate(dateRange.end)})
                </span>
              )} */}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={
                viewMode === "pie" ? (
                  <GridOn />
                ) : viewMode === "grid" ? (
                  <TableChart />
                ) : (
                  <PieChartIcon />
                )
              }
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {viewMode === "pie"
                ? "Grid"
                : viewMode === "grid"
                ? "Table"
                : "Chart"}
            </Button>

            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Error/Sample Data Notice */}
        {(error || isUsingSampleData) && (
          <Box>
            <Chip
              label={error ? error : "No data available. Showing sample data."}
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
              sx={{
                mb: 1,
                borderColor: colors.warning,
                color: colors.warning,
              }}
            />
            {isUsingSampleData && (
              <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Box>
        )}

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"}
              gap={2}
            >
              {/* Total Customers Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Customers
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {stats.totalCustomers.toLocaleString()}
                </Typography>
                {totalCustomersChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {totalCustomersChange > 0 ? (
                      <ArrowUpward
                        sx={{
                          fontSize: 14,
                          color:
                            totalCustomersChange > 0
                              ? colors.greenAccent[500]
                              : colors.redAccent[500],
                        }}
                      />
                    ) : (
                      <ArrowDownward
                        sx={{
                          fontSize: 14,
                          color:
                            totalCustomersChange > 0
                              ? colors.greenAccent[500]
                              : colors.redAccent[500],
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          totalCustomersChange > 0
                            ? colors.greenAccent[500]
                            : colors.redAccent[500],
                        ml: 0.5,
                      }}
                    >
                      {Math.abs(totalCustomersChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* New Customers Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  New Customers
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {stats.newCustomerPercentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  {data.newCustomers.toLocaleString()} customers
                </Typography>
              </Paper>

              {/* Repeat Customers Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Repeat Customers
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {stats.repeatCustomerPercentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  {data.repeatCustomers.toLocaleString()} customers
                </Typography>
                {/* <Typography variant="caption" display="block" mt={0.5} color={colors.primary[500]}>
                  Retention: {stats.retentionRate}%
                </Typography> */}
              </Paper>

              {/* Total Revenue Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  Rs. {stats.totalRevenue.toLocaleString()}
                </Typography>
                {totalRevenueChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {totalRevenueChange > 0 ? (
                      <ArrowUpward
                        sx={{
                          fontSize: 14,
                          color:
                            totalRevenueChange > 0
                              ? colors.greenAccent[500]
                              : colors.redAccent[500],
                        }}
                      />
                    ) : (
                      <ArrowDownward
                        sx={{
                          fontSize: 14,
                          color:
                            totalRevenueChange > 0
                              ? colors.greenAccent[500]
                              : colors.redAccent[500],
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          totalRevenueChange > 0
                            ? colors.greenAccent[500]
                            : colors.redAccent[500],
                        ml: 0.5,
                      }}
                    >
                      {Math.abs(totalRevenueChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart Area */}
        <Box flex={1} minHeight={250} position="relative" mt={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "pie" ? (
            <Box
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={CONFIG.chart.outerRadius}
                    innerRadius={CONFIG.chart.innerRadius}
                    dataKey="value"
                    nameKey="name"
                    animationDuration={chartAnimation ? 500 : 0}
                    animationEasing="ease-out"
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        stroke={theme.palette.background.paper}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                  />
                  <Legend
                    layout={isMobile ? "horizontal" : "vertical"}
                    verticalAlign={isMobile ? "bottom" : "middle"}
                    align={isMobile ? "center" : "right"}
                    wrapperStyle={{
                      paddingLeft: isMobile ? 0 : "20px",
                      paddingTop: isMobile ? "10px" : 0,
                    }}
                    formatter={(value, entry, index) => {
                      const total = stats.totalCustomers;
                      const percentage = Math.round(
                        (chartData[index].value / total) * 100
                      );
                      return `${value} (${percentage}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          ) : viewMode === "grid" ? (
            <Box
              display="grid"
              gridTemplateColumns={
                isMobile ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))"
              }
              gap={2}
              height="100%"
              overflow="auto"
              p={1}
              sx={{
                "&::-webkit-scrollbar": {
                  height: 6,
                  width: 6,
                },
                "&::-webkit-scrollbar-track": {
                  background: theme.palette.divider,
                  borderRadius: 10,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.palette.text.secondary,
                  borderRadius: 10,
                },
              }}
            >
              {chartData.map((customerType, index) => (
                <Paper
                  key={customerType.name}
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${
                      customerType.color
                    } 0%, ${
                      theme.palette.augmentColor({
                        color: { main: customerType.color },
                      }).dark
                    } 100%)`,
                    color: "white",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: 100,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.shadows[6],
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {customerType.name}
                    </Typography>
                    <Badge
                      badgeContent={index + 1}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          right: -5,
                          top: -5,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {customerType.name === "New Customers"
                        ? data.newCustomers.toLocaleString()
                        : data.repeatCustomers.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {customerType.name === "New Customers"
                        ? `${stats.newCustomerPercentage}% of total`
                        : `${stats.repeatCustomerPercentage}% of total`}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell align="right">New Customers</TableCell>
                    <TableCell align="right">Repeat Customers</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Customers
                    </TableCell>
                    <TableCell align="right">
                      {data.newCustomers.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {data.repeatCustomers.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {stats.totalCustomers.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Revenue
                    </TableCell>
                    <TableCell align="right">
                      Rs. {data.newCustomerRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {data.repeatCustomerRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {stats.totalRevenue.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Orders
                    </TableCell>
                    <TableCell align="right">
                      {data.newCustomerOrders.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {data.repeatCustomerOrders.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {stats.totalOrders.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Percentage
                    </TableCell>
                    <TableCell align="right">
                      {stats.newCustomerPercentage}%
                    </TableCell>
                    <TableCell align="right">
                      {stats.repeatCustomerPercentage}%
                    </TableCell>
                    <TableCell align="right">100%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Avg. Value
                    </TableCell>
                    <TableCell align="right">
                      Rs. {stats.avgNewCustomerValue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {stats.avgRepeatCustomerValue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs.{" "}
                      {(
                        stats.totalRevenue / stats.totalCustomers
                      ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerTypeRatio;
